// components/QuickStart/QuickStartModal.js
// Quick-start for new users: website + language → an inbound agent that knows
// the business → a live test in the browser. Shown once after signup, and
// from the Getting Started page.
import React, { useEffect, useRef, useState } from 'react';
import { Modal } from 'reactstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppContext } from '../../context/AppContext';
import { createNewAgent, createPrompt, generateQuickStart, getVoicePreview, getVoices } from '../../helper/backend-methods';
import { getDefaultDataCollection } from '../../utils/defaultDataCollection';
import { AGENT_LANGUAGES, assignAgentNames, getAgentLanguageLabel, getBrowserAgentLanguage } from '../../data/agentLanguages';
import QuickStartTalk from './QuickStartTalk';
import "../../assets/css/--component-quick-start.css";

const STEP = {
    WELCOME: "welcome",
    BUSINESS: "business",
    BUILDING: "building",
    DESCRIBE: "describe",
    VOICE: "voice",
    TALK: "talk",
    READY: "ready",
};

// The backend leaves this in the prompt and greeting; we swap in the voice's name.
const AGENT_NAME_TOKEN = "[AGENT_NAME]";

// Pages where the pop-up may open by itself for a new user.
const AUTO_OPEN_PATHS = ["/", "/dashboard", "/get-started"];

const GENERIC_ERROR = "We couldn't build your agent. Please try again.";

const capitalize = (value = "") => value ? value.charAt(0).toUpperCase() + value.slice(1) : "";

// "Juliette - Warm Parisian narrator" → "Juliette". Only used to skip
// near-duplicate library voices; agents get native names from AGENT_NAMES.
const voiceFirstName = (voice) => {
    const segment = (voice?.name || "").split(/\s[-–—|:]\s|,|\(/)[0].trim();
    return segment.split(/\s+/)[0] || "Alex";
};

const voiceTags = (voice) =>
    [voice.descriptive, voice.gender].filter(Boolean).map(capitalize).join(" · ") || capitalize(voice.accent || "");

// Same model choice as the Create Agent page.
const voiceModelId = (voice) =>
    voice.verified_languages?.find(x => x.model_id === "eleven_flash_v2_5")?.model_id
    ?? voice.verified_languages?.find(x => x.model_id === "eleven_multilingual_v2")?.model_id
    ?? voice.verified_languages?.[0]?.model_id
    ?? "eleven_flash_v2_5";

// Three public library voices (never the account's own cloned ones), with
// both genders when the language has them and no two sharing a first name.
const pickThreeVoices = (voices = []) => {
    const pool = voices.filter(voice =>
        voice.category !== "cloned" && voice.category !== "generated" && voice.public_owner_id && voice.voice_id
    );
    const picked = [];
    const take = (voice) => {
        if (!voice || picked.includes(voice)) return;
        if (picked.some(other => voiceFirstName(other) === voiceFirstName(voice))) return;
        picked.push(voice);
    };
    take(pool[0]);
    take(pool.find(voice => voice.gender && pool[0]?.gender && voice.gender !== pool[0].gender));
    pool.forEach(voice => picked.length < 3 && take(voice));
    return picked.slice(0, 3);
};

const errorMessage = (err) =>
    err?.response?.data?.message || (typeof err === "string" ? err : err?.message) || GENERIC_ERROR;

const Stepper = ({ number, label }) => (
    <div className="qs-stepper">
        <div className="qs-stepper-labels">
            <strong>STEP {number} OF 3</strong>
            <span>{label}</span>
        </div>
        <div className="qs-stepper-bar" aria-hidden="true">
            {[1, 2, 3].map(n => <span key={n} className={n <= number ? "active" : ""}></span>)}
        </div>
    </div>
);

const Orb = ({ large = false, live = false }) => (
    <div className={`qs-orb${large ? " qs-orb-lg" : ""}${live ? " qs-live" : ""}`} aria-hidden="true">
        <span></span><span></span><span></span><span></span><span></span>
    </div>
);

const QuickStartModal = () => {
    const {
        quickStartEligible,
        quickStartOpen,
        quickStartSkipWelcome,
        openQuickStart,
        closeQuickStart,
        markQuickStartSeen,
        markQuickStartDone,
        tasks,
        isTrial,
        daysRemaining,
        isCompleted,
    } = useAppContext();
    const location = useLocation();
    const navigate = useNavigate();

    const [step, setStep] = useState(STEP.WELCOME);
    const [website, setWebsite] = useState("");
    const [language, setLanguage] = useState(getBrowserAgentLanguage);
    const [description, setDescription] = useState("");
    const [error, setError] = useState("");

    const [buildStage, setBuildStage] = useState(0);
    const [buildFromDescription, setBuildFromDescription] = useState(false);
    const [result, setResult] = useState(null);
    const [voices, setVoices] = useState([]);
    const [selectedVoice, setSelectedVoice] = useState(null);
    const [playingVoiceId, setPlayingVoiceId] = useState(null);
    const [loadingVoiceId, setLoadingVoiceId] = useState(null);
    const [customGreeting, setCustomGreeting] = useState(null);
    const [editingGreeting, setEditingGreeting] = useState(false);

    const [creating, setCreating] = useState(false);
    const [createdPrompt, setCreatedPrompt] = useState(null);
    const [createdAgent, setCreatedAgent] = useState(null);

    const buildIdRef = useRef(0);
    const audioRef = useRef(null);
    // Spoken greetings already fetched, by voice and text, as object URLs.
    const previewCacheRef = useRef(new Map());
    // Set when the backend says text-to-speech isn't enabled; Listen then
    // plays the voice's stock sample instead.
    const ttsUnavailableRef = useRef(false);
    const previewRequestRef = useRef(0);

    // Open once by itself for a new user who hasn't started anything yet.
    useEffect(() => {
        const trialOver = isTrial && daysRemaining < 1;
        if (quickStartEligible && isTrial !== null && !trialOver && AUTO_OPEN_PATHS.includes(location.pathname)) {
            markQuickStartSeen();
            openQuickStart();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [quickStartEligible, isTrial, daysRemaining, location.pathname]);

    // Fresh start every time it opens.
    useEffect(() => {
        if (!quickStartOpen) return;
        buildIdRef.current += 1;
        setStep(quickStartSkipWelcome ? STEP.BUSINESS : STEP.WELCOME);
        setError("");
        setDescription("");
        setBuildStage(0);
        setBuildFromDescription(false);
        setResult(null);
        setVoices([]);
        setSelectedVoice(null);
        setCustomGreeting(null);
        setEditingGreeting(false);
        setCreatedPrompt(null);
        setCreatedAgent(null);
        previewCacheRef.current.forEach(url => URL.revokeObjectURL(url));
        previewCacheRef.current.clear();
        ttsUnavailableRef.current = false;
    }, [quickStartOpen, quickStartSkipWelcome]);

    const stopPreview = () => {
        previewRequestRef.current += 1;
        setLoadingVoiceId(null);
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current = null;
        }
        setPlayingVoiceId(null);
    };

    useEffect(() => {
        if (step !== STEP.VOICE) stopPreview();
    }, [step]);

    const close = () => {
        buildIdRef.current += 1;
        stopPreview();
        closeQuickStart();
    };

    const languageLabel = getAgentLanguageLabel(language);
    const agentName = selectedVoice?.agentName || "";
    const greeting = customGreeting ?? (result?.firstMessage || "").split(AGENT_NAME_TOKEN).join(agentName);

    const loadVoices = async (lang) => {
        try {
            const response = await getVoices({ language: lang, page: 0, page_size: 30 });
            return pickThreeVoices(response?.data?.voices || []);
        } catch (err) {
            console.log("Quick-start voices failed", err);
            return [];
        }
    };

    const build = async (extra = {}) => {
        const buildId = ++buildIdRef.current;
        const isCurrent = () => buildId === buildIdRef.current;

        setError("");
        setBuildStage(0);
        setBuildFromDescription(Boolean(extra.description));
        setStep(STEP.BUILDING);

        // One request does the reading and writing, so the first two ticks are
        // paced to roughly when those parts finish.
        const timers = [
            setTimeout(() => isCurrent() && setBuildStage(stage => Math.max(stage, 1)), 5000),
            setTimeout(() => isCurrent() && setBuildStage(stage => Math.max(stage, 2)), 14000),
        ];
        const voicesRequest = loadVoices(language);

        try {
            const response = await generateQuickStart({ website: website.trim(), language, ...extra });
            const data = response?.data;
            if (!isCurrent()) return;
            if (!data) throw new Error(GENERIC_ERROR);

            if (data.needsDescription) {
                setStep(STEP.DESCRIBE);
                return;
            }

            setResult(data);
            setCustomGreeting(null);
            setBuildStage(3);

            // Named in the agent's language, e.g. Elif / Emre for Turkish.
            const picked = assignAgentNames(language, await voicesRequest);
            if (!isCurrent()) return;
            if (!picked.length) {
                throw new Error(`We couldn't find ${languageLabel} voices right now. Please try again or pick another language.`);
            }
            setVoices(picked);
            setSelectedVoice(picked[0]);
            setBuildStage(4);
            setTimeout(() => isCurrent() && setStep(STEP.VOICE), 600);
        } catch (err) {
            if (!isCurrent()) return;
            setError(errorMessage(err));
            setStep(extra.description ? STEP.DESCRIBE : STEP.BUSINESS);
        } finally {
            timers.forEach(clearTimeout);
        }
    };

    const submitBusiness = (event) => {
        event.preventDefault();
        const value = website.trim();
        if (!value || !value.includes(".") || /\s/.test(value)) {
            setError("Please enter your website, like yourbusiness.com");
            return;
        }
        build();
    };

    const submitDescription = (event) => {
        event.preventDefault();
        if (description.trim().length < 10) {
            setError("Please tell us a little more, like “We sell and rent homes in Lyon”.");
            return;
        }
        build({ description: description.trim() });
    };

    // What this voice's agent would say: the edited greeting if there is one,
    // otherwise the written greeting with this voice's own name in it.
    const greetingFor = (voice) =>
        customGreeting ?? (result?.firstMessage || "").split(AGENT_NAME_TOKEN).join(voice.agentName);

    // The voice saying the greeting; the stock sample if that isn't available.
    const previewSource = async (voice) => {
        const text = greetingFor(voice).trim();
        const key = `${voice.voice_id}|${text}`;
        if (previewCacheRef.current.has(key)) return previewCacheRef.current.get(key);
        if (!text || ttsUnavailableRef.current) return voice.preview_url;

        setLoadingVoiceId(voice.voice_id);
        try {
            const response = await getVoicePreview({
                voiceId: voice.voice_id,
                text,
                language,
                public_owner_id: voice.public_owner_id,
                voice_name: voice.name,
            });
            const url = URL.createObjectURL(response.data);
            previewCacheRef.current.set(key, url);
            return url;
        } catch (err) {
            if (err?.response?.status === 503) ttsUnavailableRef.current = true;
            return voice.preview_url;
        } finally {
            setLoadingVoiceId(null);
        }
    };

    const togglePreview = async (voice, event) => {
        event.stopPropagation();
        if (playingVoiceId === voice.voice_id || loadingVoiceId === voice.voice_id) {
            stopPreview();
            return;
        }
        stopPreview();
        const requestId = ++previewRequestRef.current;
        const src = await previewSource(voice);
        // Another click, a step change or closing happened while it loaded.
        if (requestId !== previewRequestRef.current || !src) return;

        const audio = new Audio(src);
        audio.onended = () => setPlayingVoiceId(null);
        audio.play().catch(() => setPlayingVoiceId(null));
        audioRef.current = audio;
        setPlayingVoiceId(voice.voice_id);
    };

    const createAgent = async () => {
        if (!selectedVoice || !result) return;
        stopPreview();
        setCreating(true);
        setError("");

        const name = selectedVoice.agentName;
        const prompt = result.prompt.split(AGENT_NAME_TOKEN).join(name);
        const firstMessage = greeting.trim();

        try {
            // Kept across retries so a failed agent step doesn't save a second prompt.
            if (!createdPrompt) {
                const promptResponse = await createPrompt({
                    title: `${result.companyName} receptionist`,
                    prompt,
                    firstMessage,
                    purpose: result.purpose,
                    domain: result.website,
                    company: result.companyName,
                    customDataCollection: [],
                });
                setCreatedPrompt(promptResponse?.data || true);
            }

            const formData = new FormData();
            formData.append("name", `${name} (${result.companyName})`);
            formData.append("prompt", prompt);
            formData.append("firstMessage", firstMessage);
            formData.append("language", language);
            formData.append("voiceId", selectedVoice.voice_id);
            formData.append("modelId", voiceModelId(selectedVoice));
            formData.append("public_owner_id", selectedVoice.public_owner_id);
            formData.append("voice_name", selectedVoice.name);
            formData.append("type", "inbound");
            formData.append("purpose", result.purpose);
            formData.append("customDataCollection", JSON.stringify(getDefaultDataCollection()));

            const agentResponse = await createNewAgent(formData);
            const agent = agentResponse?.data;
            if (!agent?.agentId) throw new Error(GENERIC_ERROR);

            setCreatedAgent({ ...agent, voiceName: name });
            markQuickStartDone();
            setStep(STEP.TALK);
        } catch (err) {
            setError(errorMessage(err));
        } finally {
            setCreating(false);
        }
    };

    const goTo = (path) => {
        close();
        navigate(path);
    };

    const wide = step === STEP.VOICE || step === STEP.TALK;
    const host = result?.website || website.trim().replace(/^https?:\/\//i, "").replace(/\/.*$/, "");
    const buildSteps = buildFromDescription
        ? ["Reading your description", "Writing your agent’s instructions", `Writing the greeting in ${languageLabel}`, `Getting ${languageLabel} voices ready`]
        : [`Reading ${host}`, "Writing your agent’s instructions", `Writing the greeting in ${languageLabel}`, `Getting ${languageLabel} voices ready`];
    const completedTasks = tasks.filter(task => task.completed).length;
    // Once the checklist is finished, the Getting Started bar adds nothing.
    const checklistDone = isCompleted || completedTasks === tasks.length;

    return (
        <Modal
            isOpen={quickStartOpen}
            toggle={close}
            centered
            backdrop="static"
            keyboard={step !== STEP.TALK}
            className={`qs-modal${wide ? " qs-wide" : ""}`}
            aria-labelledby="qs-title"
        >
            <div className="qs-body">
                <button type="button" className="qs-close" onClick={close} aria-label="Close quick-start">
                    <i className="ri-close-line"></i>
                </button>

                {step === STEP.WELCOME && (
                    <>
                        <div className="d-flex justify-content-center"><Orb /></div>
                        <h3 id="qs-title" className="qs-title qs-center">Hear your AI agent in 60 seconds</h3>
                        <p className="qs-text qs-center">Give us your website. We’ll build a receptionist that knows your business, then you talk to it right here.</p>
                        <div className="qs-pills">
                            <span className="qs-pill">1 · Website</span>
                            <span className="qs-pill">2 · Voice</span>
                            <span className="qs-pill">3 · Talk</span>
                        </div>
                        <div className="qs-actions">
                            <button type="button" className="qs-btn qs-btn-primary" onClick={() => setStep(STEP.BUSINESS)}>Build my agent</button>
                        </div>
                        <button type="button" className="qs-link" onClick={close}>Skip, I’ll set it up myself</button>
                    </>
                )}

                {step === STEP.BUSINESS && (
                    <form className="d-flex flex-column gap-3" onSubmit={submitBusiness} noValidate>
                        <Stepper number={1} label="Your business" />
                        <h3 id="qs-title" className="qs-title">Tell us about your business</h3>
                        <div className="qs-field">
                            <label htmlFor="qs-website">Your website</label>
                            <input
                                id="qs-website"
                                className="qs-input"
                                type="text"
                                inputMode="url"
                                autoComplete="url"
                                placeholder="yourbusiness.com"
                                value={website}
                                onChange={(e) => { setWebsite(e.target.value); setError(""); }}
                                autoFocus
                            />
                            <p className="qs-hint">We only read public pages.</p>
                        </div>
                        <div className="qs-field">
                            <label htmlFor="qs-language">Language your agent speaks</label>
                            <select id="qs-language" className="qs-input" value={language} onChange={(e) => setLanguage(e.target.value)}>
                                {AGENT_LANGUAGES.map(option => (
                                    <option key={option.code} value={option.code}>
                                        {option.label}{option.native !== option.label ? ` (${option.native})` : ""}
                                    </option>
                                ))}
                            </select>
                            <p className="qs-hint">Picked from your browser. {AGENT_LANGUAGES.length} languages available.</p>
                        </div>
                        {error && <p className="qs-error" role="alert">{error}</p>}
                        <div className="qs-actions">
                            {!quickStartSkipWelcome && (
                                <button type="button" className="qs-btn qs-btn-ghost" onClick={() => { setError(""); setStep(STEP.WELCOME); }}>Back</button>
                            )}
                            <button type="submit" className="qs-btn qs-btn-primary">Build my agent</button>
                        </div>
                    </form>
                )}

                {step === STEP.BUILDING && (
                    <>
                        <Stepper number={1} label="Building" />
                        <h3 id="qs-title" className="qs-title">Building your agent…</h3>
                        <ul className="qs-checklist" aria-live="polite">
                            {buildSteps.map((label, index) => {
                                const state = index < buildStage ? "done" : index === buildStage ? "current" : "";
                                return (
                                    <li key={label} className={state}>
                                        <span className="qs-check">
                                            {state === "done" && <i className="ri-check-line"></i>}
                                            {state === "current" && <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>}
                                        </span>
                                        {label}
                                    </li>
                                );
                            })}
                        </ul>
                        {result && (
                            <div className="qs-found">
                                <p className="qs-eyebrow">What we found</p>
                                <strong>{result.companyName}</strong>
                                {result.summary && <p className="qs-text">{result.summary}</p>}
                            </div>
                        )}
                        <p className="qs-hint qs-center">Usually 20–40 seconds</p>
                    </>
                )}

                {step === STEP.DESCRIBE && (
                    <form className="d-flex flex-column gap-3" onSubmit={submitDescription} noValidate>
                        <Stepper number={1} label="Your business" />
                        <div className="qs-icon-tile warning"><i className="ri-error-warning-line"></i></div>
                        <h3 id="qs-title" className="qs-title">We couldn’t read that site</h3>
                        <p className="qs-text">It may be down, blocked, or too new for us to find anything. Tell us in one line what your business does instead.</p>
                        <div className="qs-field">
                            <label htmlFor="qs-description">What does your business do?</label>
                            <textarea
                                id="qs-description"
                                className="qs-input"
                                maxLength={500}
                                placeholder="We sell and rent homes in Lyon"
                                value={description}
                                onChange={(e) => { setDescription(e.target.value); setError(""); }}
                                autoFocus
                            />
                        </div>
                        {error && <p className="qs-error" role="alert">{error}</p>}
                        <div className="qs-actions">
                            <button type="button" className="qs-btn qs-btn-ghost" onClick={() => { setError(""); setStep(STEP.BUSINESS); }}>Back</button>
                            <button type="submit" className="qs-btn qs-btn-primary">Continue</button>
                        </div>
                    </form>
                )}

                {step === STEP.VOICE && result && (
                    <>
                        <Stepper number={2} label="Pick a voice" />
                        <h3 id="qs-title" className="visually-hidden">Pick a voice</h3>
                        <div className="qs-voices" role="radiogroup" aria-label="Voices">
                            {voices.map(voice => {
                                const selected = selectedVoice?.voice_id === voice.voice_id;
                                return (
                                    <div
                                        key={voice.voice_id}
                                        className={`qs-voice${selected ? " selected" : ""}`}
                                        role="radio"
                                        aria-checked={selected}
                                        tabIndex={0}
                                        onClick={() => setSelectedVoice(voice)}
                                        onKeyDown={(e) => {
                                            if (e.key !== "Enter" && e.key !== " ") return;
                                            e.preventDefault();
                                            setSelectedVoice(voice);
                                        }}
                                    >
                                        {selected && <span className="qs-voice-check" aria-hidden="true"><i className="ri-check-line"></i></span>}
                                        <span className="qs-voice-avatar" aria-hidden="true">{voice.agentName.charAt(0)}</span>
                                        <span className="qs-voice-name">{voice.agentName}</span>
                                        <span className="qs-voice-tags">{voiceTags(voice)}</span>
                                        <button
                                            type="button"
                                            className="qs-voice-play"
                                            onClick={(e) => togglePreview(voice, e)}
                                            aria-label={`Hear ${voice.agentName} say the greeting`}
                                        >
                                            {loadingVoiceId === voice.voice_id ? (
                                                <><span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>{" "}Loading</>
                                            ) : (
                                                <><i className={playingVoiceId === voice.voice_id ? "ri-pause-fill" : "ri-play-fill"}></i>{" "}
                                                {playingVoiceId === voice.voice_id ? "Stop" : "Listen"}</>
                                            )}
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                        <div className="qs-field">
                            <div className="qs-greeting-head">
                                <label htmlFor="qs-greeting">First thing callers hear</label>
                                <button type="button" className="qs-link" onClick={() => {
                                    if (!editingGreeting) setCustomGreeting(greeting);
                                    setEditingGreeting(!editingGreeting);
                                }}>
                                    {editingGreeting ? "Done" : "Edit"}
                                </button>
                            </div>
                            {editingGreeting ? (
                                <textarea
                                    id="qs-greeting"
                                    className="qs-input"
                                    maxLength={400}
                                    value={customGreeting ?? greeting}
                                    onChange={(e) => setCustomGreeting(e.target.value)}
                                />
                            ) : (
                                <p id="qs-greeting" className="qs-greeting">“{greeting}”</p>
                            )}
                        </div>
                        {error && <p className="qs-error" role="alert">{error}</p>}
                        <div className="qs-actions">
                            <button type="button" className="qs-btn qs-btn-ghost" disabled={creating} onClick={() => { setError(""); setStep(STEP.BUSINESS); }}>Back</button>
                            <button type="button" className="qs-btn qs-btn-primary" disabled={creating || !selectedVoice || !greeting.trim()} onClick={createAgent}>
                                {creating ? <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Creating your agent…</> : "Create my agent"}
                            </button>
                        </div>
                    </>
                )}

                {step === STEP.TALK && createdAgent && (
                    <QuickStartTalk
                        agent={createdAgent}
                        companyName={result?.companyName}
                        stepper={<Stepper number={3} label="Talk to it" />}
                        orb={(live) => <Orb large live={live} />}
                        onFinished={() => setStep(STEP.READY)}
                        onChoosePlan={() => goTo("/pricing-plans")}
                    />
                )}

                {step === STEP.READY && createdAgent && (
                    <>
                        <div className="qs-notice">
                            <div className="qs-icon-tile success"><i className="ri-checkbox-circle-line"></i></div>
                            <h3 id="qs-title" className="qs-title">{createdAgent.voiceName} is ready</h3>
                            <p className="qs-text">Saved in Agents. Most people do one of these next:</p>
                        </div>
                        <div className="qs-next">
                            <button type="button" className="qs-next-row" onClick={() => goTo(`/update-agent/${createdAgent._id}`)}>
                                <span className="qs-icon-tile"><i className="ri-settings-3-line"></i></span>
                                <span><strong>Fine-tune what {createdAgent.voiceName} says</strong><small>Edit the instructions and greeting</small></span>
                                <i className="ri-arrow-right-line qs-arrow"></i>
                            </button>
                            <button type="button" className="qs-next-row" onClick={() => goTo("/make-call")}>
                                <span className="qs-icon-tile"><i className="ri-phone-line"></i></span>
                                <span><strong>Call {createdAgent.voiceName} from your phone</strong><small>Hear your agent on a real phone line</small></span>
                                <i className="ri-arrow-right-line qs-arrow"></i>
                            </button>
                            {isTrial ? (
                                <button type="button" className="qs-next-row" onClick={() => goTo("/pricing-plans")}>
                                    <span className="qs-icon-tile"><i className="ri-star-line"></i></span>
                                    <span><strong>Choose a plan</strong><small>Keep your agent live after the trial</small></span>
                                    <i className="ri-arrow-right-line qs-arrow"></i>
                                </button>
                            ) : (
                                <button type="button" className="qs-next-row" onClick={() => goTo("/agents")}>
                                    <span className="qs-icon-tile"><i className="ri-user-voice-line"></i></span>
                                    <span><strong>See all your agents</strong><small>{createdAgent.voiceName} is in the list with the others</small></span>
                                    <i className="ri-arrow-right-line qs-arrow"></i>
                                </button>
                            )}
                        </div>
                        {!checklistDone && (
                            <div>
                                <div className="qs-progress-head">
                                    <strong>Getting started</strong>
                                    <span className="text-muted">{completedTasks} of {tasks.length} done</span>
                                </div>
                                <div className="qs-progress-bar" aria-hidden="true">
                                    {tasks.map(task => <span key={task.id} className={task.completed ? "done" : ""}></span>)}
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </Modal>
    );
};

export default QuickStartModal;
